import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { EntitlementDeniedState, LoadingState } from "@odingard/design-system";
import { useAuth } from "../lib/auth";
import { useAsync } from "../lib/useAsync";
import { api } from "../lib/api";

// Fail-closed route guard for Proposal AI. Requires an active organization and
// the given entitlement (default: the primary proposal_ai.enabled gate). While
// entitlements load the guard shows a loading state; on denial it shows the
// shared Studio upgrade prompt rather than the protected content.
export function ProposalGuard({
  children,
  entitlement = "proposal_ai.enabled",
}: {
  children: ReactNode;
  entitlement?: string;
}) {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(async () => (await api.entitlements(orgId)).entitlements, [orgId]);

  if (state.loading) return <LoadingState label="Checking your access…" />;
  const entitled =
    state.data?.some((e) => e.key === entitlement && ["ALLOWED", "TRIAL"].includes(e.status)) ?? false;
  if (!entitled) {
    return <EntitlementDeniedState product="Proposal AI" onUpgrade={() => navigate("/pricing")} />;
  }
  return <>{children}</>;
}
